import { Fieldset, Label, Textarea } from "nhsuk-react-components"
import { useContext, useState } from "react"
import { REQUIRED_ERROR_DEFINITION_URL } from "../../constants/customExtensionUrls"
import { QuestionnaireContext } from "../../context/QuestionnaireContext"
import { getResponseForQuestion, getStringValueFromResponse } from "../../fhirUtils/questionnaireUtils"
import type { QuestionProps } from "./Question"
import { questionClass } from "./questionClass"
import { QuestionnaireError } from "./QuestionnaireError"
import { showQuestion } from "./showQuestion"

const TEXT_AREA_CHARACTER_LIMIT = 3000

export const TextQuestion = ({ question, saveResponses, topLevel }: QuestionProps) => {
  // Questionnaire Context
  const questionnaireContext = useContext(QuestionnaireContext)
  if (!questionnaireContext) {
    throw new Error("TextQuestion must be used within a QuestionnaireProvider")
  }
  const { responses, errors } = questionnaireContext

  const [answer, setAnswer] = useState<string>(getStringValueFromResponse(getResponseForQuestion(question, responses)))

  if (!showQuestion(question, responses)) {
    return null
  }

  const updateAnswers = (updatedAnswer: string): void => {
    setAnswer(updatedAnswer)

    const valueString = updatedAnswer.trim()
    if (valueString.length > 0) {
      saveResponses({
        linkId: question.linkId,
        text: question.text,
        answer: [
          {
            valueString,
          },
        ],
      })
    } else {
      saveResponses({
        linkId: question.linkId,
        text: question.text,
      })
    }
  }

  const showRequiredError = !!errors[question.linkId]?.required

  const requiredErrorMessage = showRequiredError
    ? (question.extension?.find((ext) => ext.url === REQUIRED_ERROR_DEFINITION_URL)?.valueMarkdown ??
      "This field is required.")
    : null

  return (
    <div className={questionClass(question, responses)}>
      {showRequiredError && !answer && (
        <QuestionnaireError errorMessage={requiredErrorMessage} formElementId={question.linkId} />
      )}
      <Fieldset>
        {topLevel ? (
          <Fieldset.Legend isPageHeading={topLevel}>{question.text}</Fieldset.Legend>
        ) : (
          <Label htmlFor={question.linkId}>{question.text}</Label>
        )}
        <p className="text-question-hint-text">
          Do not enter medical information or requests for care. If you need help with your condition contact your care
          provider directly, use <a href="https://111.nhs.uk/">NHS 111 Online</a>, or call NHS <a href="tel:111">111</a>
          .
        </p>
        <Textarea
          aria-invalid={showRequiredError}
          error={showRequiredError}
          id={question.linkId}
          name={question.linkId}
          rows={5}
          maxLength={TEXT_AREA_CHARACTER_LIMIT}
          value={answer}
          onChange={(event: React.FormEvent<HTMLTextAreaElement>) => {
            updateAnswers(event.currentTarget.value)
          }}
        />
        <p className="text-question-character-count">
          {answer.length}/{TEXT_AREA_CHARACTER_LIMIT}
        </p>
      </Fieldset>
    </div>
  )
}
